// Demo Chat Service - For screenshots without Firebase
export interface ChatMessage {
  id: string;
  chatId: string;
  senderId: string;
  senderType: 'customer' | 'business';
  text: string;
  timestamp: Date;
  read: boolean;
  type: 'text' | 'image' | 'file' | 'appointment' | 'payment';
}

export interface Chat {
  id: string;
  participants: string[];
  lastMessage: string;
  lastMessageTime: Date;
  created: Date;
  updated: Date;
}

class DemoChatService {
  private chats: Map<string, Chat> = new Map();
  private messages: Map<string, ChatMessage[]> = new Map();

  // Create or get existing chat between customer and business
  async getOrCreateChat(customerId: string, businessId: string): Promise<string> {
    console.log('💬 Demo: Creating/getting chat for', customerId, businessId);
    
    const chatId = `demo_chat_${customerId}_${businessId}`;
    
    if (!this.chats.has(chatId)) {
      const chat: Chat = {
        id: chatId,
        participants: [customerId, businessId],
        lastMessage: '',
        lastMessageTime: new Date(),
        created: new Date(),
        updated: new Date()
      };
      this.chats.set(chatId, chat);
      this.messages.set(chatId, []);
    }
    
    return chatId;
  }

  // Send message
  async sendMessage(
    chatId: string, 
    senderId: string, 
    senderType: 'customer' | 'business', 
    text: string,
    type: 'text' | 'image' | 'file' | 'appointment' | 'payment' = 'text'
  ): Promise<void> {
    console.log('💬 Demo: Sending message', { chatId, senderId, senderType, text, type });
    
    const message: ChatMessage = {
      id: `msg_${Date.now()}_${Math.random()}`,
      chatId,
      senderId,
      senderType,
      text,
      timestamp: new Date(),
      read: false,
      type
    };
    
    const chatMessages = this.messages.get(chatId) || [];
    chatMessages.push(message);
    this.messages.set(chatId, chatMessages);
    
    // Update chat last message
    const chat = this.chats.get(chatId);
    if (chat) {
      chat.lastMessage = text;
      chat.lastMessageTime = new Date();
      chat.updated = new Date();
    }
  }

  // Get chat messages
  async getChatMessages(chatId: string): Promise<ChatMessage[]> {
    console.log('💬 Demo: Getting messages for chat', chatId);
    return this.messages.get(chatId) || [];
  }

  // Get user chats
  async getUserChats(userId: string): Promise<Chat[]> {
    console.log('💬 Demo: Getting chats for user', userId);
    
    const userChats: Chat[] = [];
    for (const chat of this.chats.values()) {
      if (chat.participants.includes(userId)) {
        userChats.push(chat);
      }
    }
    
    return userChats.sort((a, b) => b.lastMessageTime.getTime() - a.lastMessageTime.getTime());
  }

  // Mark messages as read
  async markMessagesAsRead(chatId: string, userId: string): Promise<void> {
    console.log('💬 Demo: Marking messages as read', { chatId, userId });
    
    const messages = this.messages.get(chatId) || [];
    messages.forEach(message => {
      if (message.senderId !== userId) {
        message.read = true;
      }
    });
  }

  // Get unread message count
  async getUnreadMessageCount(userId: string): Promise<number> {
    console.log('💬 Demo: Getting unread count for user', userId);
    
    let unreadCount = 0;
    for (const messages of this.messages.values()) {
      for (const message of messages) {
        if (message.senderId !== userId && !message.read) {
          unreadCount++;
        }
      }
    }
    
    return unreadCount;
  }

  // Alias for compatibility
  async getUnreadCount(userId: string): Promise<number> {
    return this.getUnreadMessageCount(userId);
  }

  // Alias for compatibility
  async markAsRead(chatId: string, userId: string): Promise<void> {
    return this.markMessagesAsRead(chatId, userId);
  }

  // Subscribe to messages (demo version)
  subscribeToMessages(chatId: string, callback: (messages: ChatMessage[]) => void) {
    console.log('💬 Demo: Subscribing to messages for chat', chatId);
    
    // Add demo messages if none exist
    if (!this.messages.has(chatId)) {
      this.addDemoMessages(chatId);
    }
    
    // Return current messages immediately
    const messages = this.messages.get(chatId) || [];
    callback(messages);
    
    // Return a dummy unsubscribe function
    return () => {
      console.log('💬 Demo: Unsubscribing from messages');
    };
  }

  // Add demo messages for screenshots
  addDemoMessages(chatId: string) {
    const demoMessages: ChatMessage[] = [
      {
        id: 'demo_msg_1',
        chatId,
        senderId: 'customer_1',
        senderType: 'customer',
        text: 'Hi! I\'d like to book an appointment for a haircut.',
        timestamp: new Date(Date.now() - 3600000), // 1 hour ago
        read: true,
        type: 'text'
      },
      {
        id: 'demo_msg_2',
        chatId,
        senderId: 'business_1',
        senderType: 'business',
        text: 'Hello! I\'d be happy to help you book an appointment. What time works best for you?',
        timestamp: new Date(Date.now() - 3500000), // 55 minutes ago
        read: true,
        type: 'text'
      },
      {
        id: 'demo_msg_3',
        chatId,
        senderId: 'customer_1',
        senderType: 'customer',
        text: 'How about tomorrow at 2 PM?',
        timestamp: new Date(Date.now() - 3000000), // 50 minutes ago
        read: true,
        type: 'text'
      },
      {
        id: 'demo_msg_4',
        chatId,
        senderId: 'business_1',
        senderType: 'business',
        text: 'Perfect! I have 2 PM available tomorrow. I\'ll send you a confirmation shortly.',
        timestamp: new Date(Date.now() - 2900000), // 48 minutes ago
        read: false,
        type: 'text'
      }
    ];
    
    this.messages.set(chatId, demoMessages);
    
    // Update chat with last message
    const chat = this.chats.get(chatId);
    if (chat) {
      chat.lastMessage = 'Perfect! I have 2 PM available tomorrow. I\'ll send you a confirmation shortly.';
      chat.lastMessageTime = new Date(Date.now() - 2900000);
    }
  }
}

export const demoChatService = new DemoChatService();
